import { useMemo } from "react";
import {
  CellClassParams,
  ColDef,
  ColGroupDef,
  ICellRendererParams,
  ValueGetterParams
} from "ag-grid-enterprise";
import { useParams } from "react-router";
import {
  coldefDateField,
  coldefHiddenField,
  coldefLinkedActivities,
  coldefNavigation,
  coldefSingleField,
  coldefUser
} from "entity-app/shared-components/log-render-components/col-defs";
import {
  ArrayStringComparator,
  ArrayStringGetQuickFilterText,
  featureStatusCellRenderer,
  materialEarlyReleaseCellRenderer,
  twoRowCellRenderer,
  twoRowGetQuickFilterText
} from "entity-app/shared-components/log-render-components/cell-renders";
import {
  CellValueRendererWithEditIcon,
  dueDateCellRenderer,
  LBSCellRenderer,
  leadTimeHeaderComponent,
  materialActionCellRenderer,
  materialDueDateHeaderComponent,
  MaterialLinksCellRenderer,
  nextDeadlineCellRenderer,
  riskCellRenderer,
  specSectionLabel,
  SubmittalLinksEntityCellRenderer
} from "components/cell-renders";
import {
  numberCellEditorParams,
  numberFilterParams
} from "components/cell-editor/numberCellEditor";
import { EWorkflowStatusDataBlock } from "constants/index";
import { FeatureTypes } from "entity-app/constants";
import { MeasurementSelectEditor } from "components/cell-editor/measurement-select-editor";
import { FeatureSpecSectionCellEditor } from "components/cell-editor/spec-section-editor";
import { LBSFieldCellEditor } from "components/cell-editor/lbs-cell-editor";
import WarrantyCellEditor, {
  WarrantyValueFormatter
} from "components/cell-editor/warranty-cell-editor";
import ShippingLocationCellEditor from "components/cell-editor/shipping-location-cell-editor";
import { DateUtils } from "utils/dateutils";
import CustomTreeFilter from "components/grid-filters/custom-tree-filter";
import CustomDateCellEditor from "components/cell-editor/date-cell-editor";
import LinkedSubmittalInnerHeader from "../../../components/header-ag-grid-component/custom-ag-grid-header-component";
import { TMaterialLog } from "../model";

const materialCharacteristicsColIds = [
  "shipping_location",
  "manufacturer",
  "part_number",
  "serial_number",
  "warranty",
  "quantity_unit",
  "material_size_unit"
];

export const useMaterialLogAgGrid = (props: {
  featureKey: string;
  milestonesColumns: any;
  cannotWriteAccessOfMaterial: boolean;
  materialHeaderMap: any;
  isLBSFeatureEnabled: Boolean;
  canSetLBSForMaterial: boolean;
}) => {
  const {
    featureKey,
    milestonesColumns,
    cannotWriteAccessOfMaterial,
    materialHeaderMap,
    isLBSFeatureEnabled,
    canSetLBSForMaterial
  } = props;

  const { projectId } = useParams() as any;
  const agGridCheckFieldEditable = (params: CellClassParams<any, any>) => {
    const { data, colDef } = params;
    const colId = colDef.colId || "";
    const fieldEditOnlyWhenNotStarted = [
      "planned_float",
      "date_block_template_name"
    ];
    if (
      fieldEditOnlyWhenNotStarted.includes(colId) &&
      data.workflow_status !== EWorkflowStatusDataBlock.WF_NOT_STARTED
    )
      return false;
    const fieldEditWhenNotComplete = ["manual_entry_date"];
    if (
      fieldEditWhenNotComplete.includes(colId) &&
      data.workflow_status === EWorkflowStatusDataBlock.WF_COMPLETED
    )
      return false;
    return true;
  };

  const inlineEditPermissions = (params: CellClassParams<any, any>) => {
    if (!cannotWriteAccessOfMaterial) return true;
    const { colDef } = params;
    const colId = colDef.colId || "";
    if (materialCharacteristicsColIds.includes(colId)) {
      return params.context.canInlineEditMaterialCharacteristics;
    }
    return false;
  };

  const isGridCellEditable = (params: CellClassParams<any, any>) => {
    return (
      params?.colDef?.editable &&
      inlineEditPermissions(params) &&
      agGridCheckFieldEditable(params)
    );
  };

  const defaultColDef = useMemo(
    () =>
      ({
        sortable: true,
        editable: false,
        filter: true,
        resizable: true,
        menuTabs: ["filterMenuTab"],
        minWidth: 100,
        cellRenderer: CellValueRendererWithEditIcon,
        cellEditorPopup: false
      } as ColDef<TMaterialLog>),
    []
  );

  const columnDefs = useMemo(
    () =>
      [
        coldefNavigation(
          {
            colId: "material_id",
            field: "sequence_id",
            headerName: materialHeaderMap?.material_id,
            pinned: "left",
            lockPosition: "left",
            initialWidth: 80,
            sort: "asc",
            lockVisible: true,
            filter: false
          },
          projectId,
          featureKey
        ),
        coldefSingleField({
          colId: "name",
          field: "title",
          headerName: materialHeaderMap?.name,
          initialWidth: 180,
          pinned: "left",
          lockPosition: "left",
          cellRenderer: twoRowCellRenderer,
          cellRendererParams: {
            featureKey,
            projectId
          },
          getQuickFilterText: twoRowGetQuickFilterText,
          lockVisible: true,
          editable: true,
          filter: false
        }),
        coldefHiddenField({
          field: "description",
          headerName: materialHeaderMap?.description,
          pinned: "left",
          lockPosition: "left"
        }),
        coldefSingleField({
          colId: "status",
          field: "milestone_state",
          headerName: materialHeaderMap?.status,
          lockVisible: true,
          initialWidth: 200,
          minWidth: 200,
          tooltipField: undefined,
          tooltipValueGetter: undefined,
          editable: true,
          cellRenderer: featureStatusCellRenderer
        }),
        coldefSingleField({
          field: "material_released_early",
          headerName: materialHeaderMap?.wf_override_reason || "Released Early",
          initialWidth: 160,
          editable: false,
          cellRenderer: materialEarlyReleaseCellRenderer,
          valueGetter: ({ data }: any) => {
            if (data?.material_released_early) return "Yes";
            return "";
          }
        }),
        coldefHiddenField({
          colId: "released_early_msg",
          field: "material_released_early",
          headerName: "Reason Cited For Early Release"
        }),
        coldefDateField({
          field: "planned_next_milestone_date",
          headerName: materialHeaderMap?.planned_next_milestone_date,
          initialWidth: 160,
          valueGetter: (params: ValueGetterParams) => {
            return DateUtils.format(
              params.data.date_block?.planned_next_milestone_date
            );
          },
          cellRenderer: (params: ICellRendererParams) => {
            return nextDeadlineCellRenderer(params, false);
          }
        }),
        coldefDateField({
          field: "projected_next_milestone_date",
          headerName: materialHeaderMap?.projected_next_milestone_date,
          initialWidth: 160,
          valueGetter: (params: ValueGetterParams) => {
            return DateUtils.format(
              params.data.date_block?.projected_next_milestone_date
            );
          },
          cellRenderer: (params: ICellRendererParams) => {
            return nextDeadlineCellRenderer(params, false);
          }
        }),
        coldefDateField({
          field: "next_planned_deadline",
          headerName: materialHeaderMap?.next_planned_deadline,
          initialWidth: 160,
          cellRenderer: (params: ICellRendererParams) => {
            return nextDeadlineCellRenderer(params, false);
          }
        }),
        coldefSingleField({
          field: "next_action",
          headerName: materialHeaderMap?.next_action || "",
          initialWidth: 200,
          editable: false,
          filter: false
        }),
        coldefDateField({
          colId: "manual_entry_date",
          field: "manual_entry_date",
          headerName: materialHeaderMap?.manual_entry_date || "Deadline Date",
          editable: true,
          cellRenderer: CellValueRendererWithEditIcon,
          cellEditor: CustomDateCellEditor,
          valueGetter: undefined,
          valueFormatter: ({ value }) => (value ? DateUtils.format(value) : ""),
          cellEditorParams: {
            placeholder: `Select ${
              materialHeaderMap?.manual_entry_date || "Deadline Date"
            }`
          }
        }),
        coldefDateField({
          field: "date_block.final_deadline_date",
          colId: "final_deadline_date",
          headerName: materialHeaderMap?.final_deadline_date || "Deadline",
          headerTooltip: materialHeaderMap?.final_deadline_date || "Deadline",
          valueGetter: (params: ValueGetterParams) => {
            return DateUtils.format(
              params.data.date_block?.final_deadline_date
            );
          },
          cellRenderer: (params: ICellRendererParams) => {
            return nextDeadlineCellRenderer(params, false);
          }
        }),
        coldefDateField({
          field: "date_block.projected_last_milestone_date",
          colId: "projected_last_milestone_date",
          headerName:
            materialHeaderMap?.projected_last_milestone_date ||
            "Projected Completion Date",
          headerTooltip:
            materialHeaderMap?.projected_last_milestone_date ||
            "Projected Completion Date",
          valueGetter: (params: ValueGetterParams) => {
            return DateUtils.format(
              params.data.date_block?.projected_last_milestone_date
            );
          },
          cellRenderer: (params: ICellRendererParams) => {
            return nextDeadlineCellRenderer(params, false);
          }
        }),
        coldefDateField({
          field: "due_date",
          headerName: materialHeaderMap?.due_date,
          initialWidth: 160,
          cellRenderer: (params: ICellRendererParams) => {
            return dueDateCellRenderer(params, false);
          },
          headerComponentParams: materialDueDateHeaderComponent
        }),
        coldefSingleField({
          field: "risk_level",
          headerName: materialHeaderMap?.risk_level,
          initialWidth: 100,
          cellRenderer: (params: ICellRendererParams) => {
            return riskCellRenderer(params, false);
          }
        }),
        coldefSingleField({
          colId: "planned_float",
          field: "pre_WF_start_float",
          headerName: "Material Planned Float",
          initialWidth: 130,
          editable: true,
          cellEditor: "agNumberCellEditor",
          cellEditorParams: numberCellEditorParams,
          filter: "agNumberColumnFilter",
          filterParams: numberFilterParams
        }),
        coldefSingleField({
          field: "effective_float",
          headerName: "Material Actual Float",
          initialWidth: 130,
          filter: "agNumberColumnFilter",
          filterParams: numberFilterParams
        }),
        coldefSingleField({
          field: "lead_time",
          headerName: materialHeaderMap?.lead_time || "Total Lead Time",
          initialWidth: 100,
          editable: false,
          filter: "agNumberColumnFilter",
          filterParams: numberFilterParams,
          headerComponentParams: leadTimeHeaderComponent
        }),
        coldefSingleField({
          colId: "custom_spec_section",
          field: "spec_section",
          headerName: materialHeaderMap?.spec_section_no,
          valueGetter: ({ data }) => {
            if (!data || !data.spec_section || !data.spec_section.length)
              return "";
            const spec = data.spec_section[0];
            const specLabel = specSectionLabel({
              specNumber: spec?.number,
              specName: spec?.name
            });

            return specLabel;
          },
          editable: true,
          cellEditor: FeatureSpecSectionCellEditor,
          initialWidth: 200
        }),
        coldefSingleField({
          field: "purchase_order",
          headerName: materialHeaderMap?.purchase_order || "",
          initialWidth: 160,
          editable: true
        }),
        coldefSingleField({
          colId: "material_tag",
          field: "tag_name",
          headerName: materialHeaderMap?.tag_name || "",
          initialWidth: 160,
          editable: true
        }),
        ...(isLBSFeatureEnabled
          ? [
              coldefSingleField({
                field: "locations",
                headerName: materialHeaderMap?.locations || "",
                initialWidth: 250,
                minWidth: 250,
                maxWidth: 600,
                valueGetter: ({ data }) => {
                  if (!data || !data.locations) return [];
                  return data.locations.map((loc: any) => ({
                    id: loc.id,
                    name: loc.path,
                    path: loc.path
                  }));
                },
                filter: CustomTreeFilter,
                editable: canSetLBSForMaterial,
                cellRenderer: LBSCellRenderer,
                cellEditor: LBSFieldCellEditor,
                getQuickFilterText: ArrayStringGetQuickFilterText,
                comparator: ArrayStringComparator
              })
            ]
          : []),
        coldefUser({
          def: {
            colId: "date_block_template_name",
            headerName: materialHeaderMap?.date_block_template_name,
            editable: true,
            initialWidth: 180
          },
          user: {
            id: "template.id",
            firstName: "template.name",
            lastName: "",
            dropdownType: "Template_dropdown",
            featureTypeId: FeatureTypes.MATERIALS
          }
        }),
        coldefSingleField({
          colId: "quantity_unit",
          field: "quantity",
          headerName: materialHeaderMap?.quantity || "",
          initialWidth: 180,
          minWidth: 180,
          editable: true, // materialCharacteristicsCellsEditable,
          valueGetter: (param: any) =>
            `${param.data.quantity ?? ""} ${
              param.data.material_quantity_unit ?? ""
            }`.trim(),
          cellEditor: MeasurementSelectEditor,
          cellEditorParams: {
            type: "regular-select",
            dataMapVar: "material_quantity_unit",
            setKeyId: "quantity_unit_id",
            unitValueProp: "quantity",
            unit: "material_quantity_unit"
          }
        }),
        coldefUser({
          def: {
            colId: "trade_partner",
            headerName: materialHeaderMap?.trade_partner,
            editable: true,
            initialWidth: 220,
            lockVisible: true
          },
          user: {
            id: "responsible_contractor.id",
            firstName: "responsible_contractor.name",
            lastName: "",
            dropdownType: "RC_dropdown"
          }
        }),

        coldefUser({
          def: {
            colId: "assignee",
            field: "assignee_user",
            headerName: materialHeaderMap?.assignee,
            editable: true,
            initialWidth: 180,
            lockVisible: true
          },
          user: {
            id: "assignee_user.id",
            firstName: "assignee_user.first_name",
            lastName: "assignee_user.last_name",
            statusId: "assignee_user.status_id",
            dropdownType: "RC_ASSIGNEE_dropdown",
            dependentId: "responsible_contractor.id",
            dependentNotFoundContent: "First select a Responsible Contractor",
            companyObjField: "assignee_user.company"
          }
        }),
        coldefUser({
          def: {
            colId: "gc_representative",
            headerName: materialHeaderMap?.gc_representative,
            editable: true,
            initialWidth: 180
          },
          user: {
            id: "gc_representative.id",
            firstName: "gc_representative.first_name",
            lastName: "gc_representative.last_name",
            statusId: "gc_representative.status_id",
            companyObjField: "gc_representative.company"
          }
        }),

        coldefSingleField({
          colId: "shipping_location",
          field: "shipping_location.label",
          headerName: materialHeaderMap?.shipping_location || "",
          editable: true,
          cellEditor: ShippingLocationCellEditor
        }),
        coldefSingleField({
          field: "manufacturer",
          headerName: materialHeaderMap?.manufacturer || "",
          editable: true,
          initialWidth: 140
        }),

        coldefLinkedActivities({
          field: "tasks",
          colId: "linked_tasks",
          headerName: materialHeaderMap?.linked_activities || ""
        }),
        coldefSingleField({
          field: "materials",
          colId: "linked_materials",
          headerName: materialHeaderMap?.linked_materials || "",
          headerTooltip: materialHeaderMap?.linked_materials || "",
          cellRenderer: MaterialLinksCellRenderer,
          valueGetter: ({ data }: any) => {
            return data?.materials
              .filter(
                (item: any) =>
                  item.feature_type === FeatureTypes.MATERIALS && !item.implicit
              )
              ?.map((material: any) => {
                return material;
              });
          },
          filterParams: {
            valueFormatter: ({ value }: any) => {
              return value ? "Materials Linked" : "No Materials Linked";
            },
            keyCreator: ({ value }: any) => {
              return value ? "Materials Linked" : "No Materials Linked";
            }
          }
        }),
        coldefSingleField({
          colId: "linked_submittals",
          field: "linked_items",
          headerName: materialHeaderMap?.linked_submittals || "",
          headerComponentParams: {
            innerHeaderComponent: LinkedSubmittalInnerHeader
          },
          initialWidth: 400,
          minWidth: 400,
          resizable: false,
          cellRenderer: SubmittalLinksEntityCellRenderer,
          editable: false,
          filterParams: {
            valueFormatter: (params: any) => {
              try {
                return params?.value
                  ? "Submittal Linked"
                  : "No Submittal Linked";
              } catch {
                return "No Submittal Linked";
              }
            },
            keyCreator: (params: any) => {
              try {
                return params?.value
                  ? "Submittal Linked"
                  : "No Submittal Linked";
              } catch {
                return "No Submittal Linked";
              }
            }
          }
        }),
        coldefHiddenField({
          colId: "is_linked_void_submittal",
          headerName: "Void Submittals",
          filter: "agSetColumnFilter",
          menuTabs: ["filterMenuTab"],
          valueGetter: ({ data }: any) => {
            return data.linked_items?.some(
              (submittal: any) =>
                submittal.workflow_status === EWorkflowStatusDataBlock.VOID
            )
              ? "Voided Submittals"
              : "";
          }
        }),
        coldefHiddenField({
          colId: "workflow_status",
          field: "workflow_status",
          headerName: "Workflow Status",
          filter: "agSetColumnFilter",
          menuTabs: ["filterMenuTab"]
        }),
        coldefSingleField({
          field: "material_size_unit",
          headerName: materialHeaderMap?.material_size || "",
          editable: true,
          initialWidth: 180,
          minWidth: 180,
          valueGetter: (param: any) =>
            `${param?.data?.material_size ?? ""} ${
              param?.data?.material_size_unit || ""
            }`.trim(),
          cellEditor: MeasurementSelectEditor,
          cellEditorParams: {
            type: "regular-select",
            dataMapVar: "material_quantity_unit",
            setKeyId: "size_unit_id",
            unitValueProp: "material_size",
            unit: "material_size_unit"
          }
        }),
        coldefSingleField({
          field: "part_number",
          headerName: materialHeaderMap?.part_number || "",
          editable: true,
          initialWidth: 140
        }),
        coldefSingleField({
          field: "serial_number",
          headerName: materialHeaderMap?.serial_number || "",
          editable: true,
          initialWidth: 160
        }),
        coldefSingleField({
          field: "warranty",
          headerName: materialHeaderMap?.warranty || "",
          valueFormatter: WarrantyValueFormatter,
          editable: true,
          initialWidth: 190,
          filter: "agNumberColumnFilter",
          filterParams: numberFilterParams,
          cellEditor: WarrantyCellEditor
        }),
        coldefSingleField({
          colId: "action",
          headerName: "Actions",
          headerTooltip: "Actions",
          pinned: "right",
          initialWidth: 120,
          editable: false,
          suppressColumnsToolPanel: true,
          cellRenderer: materialActionCellRenderer
        }),
        ...milestonesColumns.groupCols,
        ...milestonesColumns.columnDefsForDateBlock
      ] as (ColDef<TMaterialLog> | ColGroupDef<TMaterialLog>)[],
    [
      materialHeaderMap,
      projectId,
      featureKey,
      isLBSFeatureEnabled,
      canSetLBSForMaterial,
      milestonesColumns.groupCols,
      milestonesColumns.columnDefsForDateBlock
    ]
  );
  return {
    columnDefs,
    defaultColDef,
    milestonesColumns,
    isGridCellEditable
  };
};
